import { Controller } from "@hotwired/stimulus"
// Venue autocomplete: debounced search against a JSON endpoint from 2
// characters, rendering rows (square photo, name, kind · area) under the
// input. Picking a row swaps the input for a selected card and reveals the
// Continue button; the card's ✕ goes back to the search.
export default class extends Controller {
  static targets = ["input", "results", "hidden", "search", "selection", "submit"]
  static values = { url: String, min: { type: Number, default: 2 }, delay: { type: Number, default: 200 } }

  disconnect() { clearTimeout(this.timer) }

  search() {
    this.hiddenTarget.value = ""
    clearTimeout(this.timer)
    const q = this.inputTarget.value.trim()
    if (q.length < this.minValue) return this.close()
    this.timer = setTimeout(() => this.fetch(q), this.delayValue)
  }

  async fetch(q) {
    const response = await fetch(`${this.urlValue}?q=${encodeURIComponent(q)}`, { headers: { Accept: "application/json" } })
    if (!response.ok) return
    if (q !== this.inputTarget.value.trim()) return // stale response
    this.render(await response.json())
  }

  render(items) {
    if (items.length === 0) return this.close()
    this.resultsTarget.innerHTML = ""
    items.forEach((item) => {
      const row = document.createElement("button")
      row.type = "button"
      row.className = "w-full flex items-center gap-3 px-3 py-2.5 text-left bg-white hover:bg-gray-50 border-none cursor-pointer transition-colors duration-300"
      row.addEventListener("click", () => this.pick(item))
      row.append(this.photoFor(item), this.textFor(item))
      this.resultsTarget.appendChild(row)
    })
    this.resultsTarget.classList.remove("hidden")
  }

  pick(item) {
    this.hiddenTarget.value = item.id
    this.close()

    this.selectionTarget.innerHTML = ""
    const card = document.createElement("div")
    card.className = "flex items-center gap-3 border border-indigo-200 bg-indigo-50/40 rounded-xl px-3 py-2.5"
    const text = this.textFor(item)
    text.classList.add("flex-1")
    const remove = document.createElement("button")
    remove.type = "button"
    remove.setAttribute("aria-label", "Remove selection")
    remove.className = "shrink-0 bg-transparent border-none cursor-pointer text-gray-400 hover:text-gray-600 transition-colors duration-300 text-base leading-none p-1"
    remove.textContent = "✕"
    remove.addEventListener("click", () => this.clear())
    card.append(this.photoFor(item), text, remove)
    this.selectionTarget.appendChild(card)

    this.searchTarget.classList.add("hidden")
    this.selectionTarget.classList.remove("hidden")
    if (this.hasSubmitTarget) this.submitTarget.classList.remove("hidden")
  }

  clear() {
    this.hiddenTarget.value = ""
    this.selectionTarget.classList.add("hidden")
    this.selectionTarget.innerHTML = ""
    this.searchTarget.classList.remove("hidden")
    if (this.hasSubmitTarget) this.submitTarget.classList.add("hidden")
    this.inputTarget.focus()
  }

  close() { this.resultsTarget.classList.add("hidden"); this.resultsTarget.innerHTML = "" }

  closeOnOutside(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  photoFor(item) {
    const photo = document.createElement(item.photo_url ? "img" : "div")
    photo.className = "w-10 h-10 rounded-lg object-cover shrink-0 bg-gray-100"
    if (item.photo_url) photo.src = item.photo_url
    return photo
  }

  textFor(item) {
    const text = document.createElement("div")
    text.className = "min-w-0"
    const name = document.createElement("div")
    name.className = "text-[13.5px] font-semibold text-[#1D1733] truncate"
    name.textContent = item.name
    const sub = document.createElement("div")
    sub.className = "text-xs text-gray-500 truncate"
    sub.textContent = [item.kind, item.area].filter(Boolean).join(" · ")
    text.append(name, sub)
    return text
  }
}
